import React, { useState, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import config from '../../config';

const TermsStep = ({ 
    worldHighStreetTermsAccepted, 
    setWorldHighStreetTermsAccepted,
    venueTermsAccepted, 
    setVenueTermsAccepted,
    venueKey
}) => {
    const [venueTermsContent, setVenueTermsContent] = useState('');
    const [venueTermsLoading, setVenueTermsLoading] = useState(true);
    const [venueTermsError, setVenueTermsError] = useState('');
    const [venueName, setVenueName] = useState('');

    // Fetch venue terms when component mounts
    useEffect(() => {
        const fetchVenueTerms = async () => {
            if (!venueKey) {
                setVenueTermsError('No venue specified');
                setVenueTermsLoading(false);
                return;
            }

            try {
                const response = await fetch(`${config.apiBaseUrl}/api/venue/public/${venueKey}/terms`);
                
                if (response.ok) {
                    const data = await response.json();
                    
                    if (!data.hasTerms || !data.termsContent) {
                        setVenueTermsError('This venue has not set up their terms and conditions yet. Please contact the venue to complete your signup.');
                    } else {
                        setVenueTermsContent(data.termsContent);
                        setVenueName(data.venueName || 'this venue');
                    }
                } else {
                    setVenueTermsError('Failed to load venue terms and conditions');
                }
            } catch (err) {
                console.error('Error fetching venue terms:', err);
                setVenueTermsError('Unable to load venue terms. Please try again later.');
            } finally {
                setVenueTermsLoading(false);
            }
        };

        fetchVenueTerms();
    }, [venueKey]);

    // Simple hash function for terms text
    const generateHash = (text) => {
        let hash = 0;
        if (text.length === 0) return hash;
        for (let i = 0; i < text.length; i++) {
            const char = text.charCodeAt(i);
            hash = ((hash << 5) - hash) + char;
            hash = hash & hash; // Convert to 32-bit integer
        }
        return Math.abs(hash).toString(16);
    };

    // Handle venue terms acceptance
    const handleVenueTermsChange = (checked) => {
        if (checked && venueTermsContent) {
            const termsHash = generateHash(venueTermsContent);
            setVenueTermsAccepted(termsHash);
        } else {
            setVenueTermsAccepted(false);
        }
    };

    return (
        <div className="space-y-6">
            {/* World High Street Terms */}
            <div className="card-luxury p-6">
                <h3 className="text-xl font-display font-semibold text-luxury-pearl mb-4">
                    World High Street Terms and Conditions
                </h3>
                <div className="bg-luxury-black border border-luxury-graphite/30 p-4 rounded-lg mb-4 max-h-60 overflow-y-auto">
                    <div className="text-luxury-pearl/70 text-sm space-y-3">
                        <p>Welcome to World High Street! By using our services, you agree to the following terms and conditions:</p>
                        <ul className="space-y-2 pl-4">
                            <li>• You must be at least 18 years old to use our services.</li>
                            <li>• All content provided on this site is for informational purposes only.</li>
                            <li>• We reserve the right to modify or discontinue our services at any time without notice.</li>
                            <li>• Users are responsible for maintaining the confidentiality of their account information.</li>
                            <li>• You agree to provide accurate and complete information when creating your account.</li>
                            <li>• You will not use our services for any illegal or unauthorized purpose.</li>
                            <li>• We may suspend or terminate your account for violations of these terms.</li>
                        </ul>
                        <p>For more detailed information, please refer to our full terms and conditions document.</p>
                    </div>
                </div>
                <label className="flex items-center cursor-pointer">
                    <input
                        type="checkbox"
                        checked={worldHighStreetTermsAccepted}
                        onChange={(e) => setWorldHighStreetTermsAccepted(e.target.checked)}
                        className="w-4 h-4 text-emerald-500 bg-luxury-graphite border-luxury-stone rounded focus:ring-emerald-500"
                    />
                    <span className="ml-2 text-sm text-luxury-pearl/80">
                        I accept the World High Street Terms and Conditions *
                    </span>
                </label>
            </div>

            {/* Venue Terms */}
            <div className="card-luxury p-6">
                <h3 className="text-xl font-display font-semibold text-luxury-pearl mb-4">
                    {venueName ? `${venueName} - ` : ''}Venue Terms and Conditions
                </h3>
                
                {venueTermsLoading ? (
                    <div className="bg-luxury-black border border-luxury-graphite/30 p-8 rounded-lg mb-4 text-center">
                        <div className="text-luxury-pearl/60">Loading venue terms...</div>
                    </div>
                ) : venueTermsError ? (
                    <div className="bg-red-600/10 border border-red-600/30 p-4 rounded-lg mb-4">
                        <div className="text-red-400 text-sm">
                            <strong>Error:</strong> {venueTermsError}
                        </div>
                        <div className="text-red-300/70 text-sm mt-2">
                            You will not be able to complete signup until the venue has configured their terms and conditions.
                        </div>
                    </div>
                ) : (
                    <>
                        <div className="bg-luxury-black border border-luxury-graphite/30 p-4 rounded-lg mb-4 max-h-96 overflow-y-auto">
                            <div className="text-luxury-pearl/70 text-sm prose prose-invert prose-emerald max-w-none">
                                <ReactMarkdown>
                                    {venueTermsContent}
                                </ReactMarkdown>
                            </div>
                        </div>
                        <label className="flex items-center cursor-pointer">
                            <input
                                type="checkbox"
                                checked={venueTermsAccepted !== false}
                                onChange={(e) => handleVenueTermsChange(e.target.checked)}
                                className="w-4 h-4 text-emerald-500 bg-luxury-graphite border-luxury-stone rounded focus:ring-emerald-500"
                                disabled={!venueTermsContent}
                            />
                            <span className="ml-2 text-sm text-luxury-pearl/80">
                                I accept the {venueName || 'venue'} Terms and Conditions *
                            </span>
                        </label>
                        {venueTermsAccepted && venueTermsAccepted !== false && (
                            <div className="mt-2 text-xs text-luxury-pearl/30">
                                Terms Version: {venueTermsAccepted}
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

export default TermsStep;
